import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

// Poll permissions-version every 30s. When admin flips a role permission or
// a user's role in the DB, `_bump_permissions_version()` on the backend
// increments the version. The next poll here detects the change, refetches
// `/auth/me` + effective permissions, and surfaces a toast so the user knows
// their access was updated (avoids the "why is this broken" support ticket).
const PERM_POLL_BASE_MS = 60_000;
// Small per-tab jitter so 30+ agents don't fire the poll at exactly the
// same second (bulk arrival used to saturate the Mongo pool during deploys
// and trigger a false "version dropped" cascade). ±5 s.
const PERM_POLL_JITTER_MS = 10_000;
const nextPollInterval = () => (
  PERM_POLL_BASE_MS + Math.floor(Math.random() * PERM_POLL_JITTER_MS * 2) - PERM_POLL_JITTER_MS
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = signed in
  const [options, setOptions] = useState(null);
  const [permissions, setPermissions] = useState(null); // effective page permissions
  const permsVersionRef = useRef(null);
  const permsHashRef = useRef(null);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return null;
    }
  }, []);

  const fetchPermissions = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me/effective-permissions");
      setPermissions(data);
      return data;
    } catch (e) {
      console.debug("[Auth] effective-permissions fetch failed:", e?.response?.status);
      return null;
    }
  }, []);

  // Self-heal poller — detects backend permission bumps and refreshes state
  const pollPermsVersion = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me/permissions-version");
      const firstPoll = permsVersionRef.current === null;
      // 🔒 Directional comparison — the server version is a monotonically
      // increasing counter (see _bump_permissions_version). If a poll comes
      // back with a version LOWER than what we already saw, something on
      // the server hiccuped (Mongo blip during a deploy dropped the read
      // to the hardcoded fallback of `1`). Ignoring the downgrade prevents
      // a false storm of "your access was updated" toasts + refetches
      // across every agent every time we deploy. Sentinel values (≤ 0)
      // are also treated as "no data — skip this poll".
      const incoming = Number(data?.version);
      const validIncoming = Number.isFinite(incoming) && incoming > 0;
      const versionChanged = !firstPoll
        && validIncoming
        && incoming > permsVersionRef.current;
      const hashChanged = !firstPoll && data.my_permissions_hash !== permsHashRef.current;
      // Only advance the ref when we saw a legit forward move — otherwise
      // keep the last-known-good so the next healthy poll compares against
      // the right baseline.
      if (validIncoming && (firstPoll || incoming > permsVersionRef.current)) {
        permsVersionRef.current = incoming;
      }
      permsHashRef.current = data.my_permissions_hash;

      if (versionChanged || hashChanged) {
        // Something in the permission graph changed — refetch me + perms.
        await Promise.all([fetchMe(), fetchPermissions()]);
        if (hashChanged) {
          // The change affects THIS user directly
          toast.info(
            "Your access was just updated by admin. If a page or button still looks stale, refresh (Ctrl/Cmd + R).",
            { duration: 8000 },
          );
          // Broadcast so any page (Leads, AdminUsers, etc.) can re-run their
          // permission-dependent effects without a full reload.
          try {
            window.dispatchEvent(new CustomEvent("permissions:updated", { detail: data }));
          } catch { /* noop */ }
        }
      }
    } catch (e) {
      // 401/network — expected pre-login; ignore silently.
      if (e?.response?.status && e.response.status !== 401) {
        console.debug("[Auth] perms-version poll failed:", e.response.status);
      }
    }
  }, [fetchMe, fetchPermissions]);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/options/all");
        setOptions(data);
      } catch (e) {
        console.error("Failed to load options", e);
      }
    })();
    // Fire an initial permission-version + effective-permissions load
    pollPermsVersion();
    fetchPermissions();
  }, [user, pollPermsVersion, fetchPermissions]);

  useEffect(() => {
    if (!user) return undefined;
    // Recursive setTimeout with fresh jitter per tick — better than
    // setInterval because each tab gets a slightly different cadence,
    // spreading the poll load evenly. Prevents the "30 agents hit
    // Mongo at second :00" clustering that used to amplify deploy blips.
    let cancelled = false;
    let timer = null;
    const schedule = () => {
      if (cancelled) return;
      timer = setTimeout(async () => {
        await pollPermsVersion();
        schedule();
      }, nextPollInterval());
    };
    schedule();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [user, pollPermsVersion]);

  // Retry-on-403 self-heal — when any API returns 403, immediately probe
  // permissions-version. If the version increased, refetch and re-fire a
  // `permissions:updated` event so pages re-run their gated logic.
  useEffect(() => {
    const handler = () => { pollPermsVersion(); };
    window.addEventListener("permissions:probe", handler);
    return () => window.removeEventListener("permissions:probe", handler);
  }, [pollPermsVersion]);

  const refreshOptions = async () => {
    try {
      const { data } = await api.get("/options/all");
      setOptions(data);
    } catch (e) {
      console.error(e);
    }
  };

  const login = async (email, password, opts = {}) => {
    const body = { email, password };
    if (opts.takeover) body.takeover = true;
    // Explicit timeout — without it a stalled backend leaves the login
    // button stuck on "Signing in…" forever (2026-08-06 report).
    const { data } = await api.post("/auth/login", body, { timeout: 25000 });
    if (data.access_token) localStorage.setItem("access_token", data.access_token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    // Shift counter continues across logout/login — only "End Shift" ends it.
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("logout failed", e?.message);
    }
    localStorage.removeItem("access_token");
    setUser(false);
    setPermissions(null);
    permsVersionRef.current = null;
    permsHashRef.current = null;
  };

  return (
    <AuthContext.Provider value={{
      user, options, permissions,
      refreshOptions, login, logout,
      refetchPermissions: fetchPermissions,
      refetchMe: fetchMe,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
